import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { FindingActionKind, ValidationResult } from "../../types";
import {
  actionMap,
  activeFindings,
  effectiveStatus,
  failGateSatisfied,
  findingId,
  noteMap,
  unaddressedFailCount,
} from "../../lib/findings";
import { findingTitle } from "./FindingMarker";
import FindingContextCard from "./FindingContextCard";

// localStorage flag for the one-time findings help banner.
const FINDINGS_HELP_KEY = "ct:findings-help-dismissed";

// User's preferred findings-panel size, persisted across sessions.
const FINDINGS_SIZE_KEY = "ct:findings-panel-size";
const PANEL_MIN_W = 320;
const PANEL_MIN_H = 200;
const PANEL_DEFAULT = { w: 400, h: 500 };

interface PanelSize {
  w: number;
  h: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

// Max the panel may grow to — bounded by the viewport so it can never
// open larger than the screen. Falls back to generous values for SSR.
function panelMax(): PanelSize {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 900;
  return { w: Math.max(PANEL_MIN_W, vw * 0.95), h: Math.max(PANEL_MIN_H, vh * 0.9) };
}

function loadPanelSize(): PanelSize {
  const max = panelMax();
  try {
    const raw = localStorage.getItem(FINDINGS_SIZE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PanelSize>;
      if (typeof p.w === "number" && typeof p.h === "number") {
        return {
          w: clamp(p.w, PANEL_MIN_W, max.w),
          h: clamp(p.h, PANEL_MIN_H, max.h),
        };
      }
    }
  } catch {
    /* unparseable / storage blocked — fall through to default */
  }
  // Default, but never larger than the viewport (small screens).
  return {
    w: clamp(PANEL_DEFAULT.w, PANEL_MIN_W, max.w),
    h: clamp(PANEL_DEFAULT.h, PANEL_MIN_H, max.h),
  };
}

function savePanelSize(s: PanelSize): void {
  try {
    localStorage.setItem(FINDINGS_SIZE_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable — size just resets next session */
  }
}

// Verification status pill — and the PRIMARY way to access findings.
// Clicking the pill toggles a dropdown listing every (non-dismissed)
// finding with Dismiss / Accept / Flag-for-edit buttons. This always
// works regardless of whether inline markers managed to map to the text.
//
// While Tier 2 runs in the background the parent passes `verifying` and we
// show a neutral "Verifying…" pill.
export default function ValidationBadge({
  validation,
  verifying,
  onAction,
  compact,
}: {
  validation: ValidationResult | null | undefined;
  verifying?: boolean;
  // When provided, each finding row shows action buttons. Omit for a
  // read-only list (e.g. the export aggregate — act on the source surface).
  onAction?: (findingId: string, kind: FindingActionKind, note?: string) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  // The finding row whose note input is open, plus its draft text. Only one
  // row can be in the note-editing state at a time.
  const [flaggingId, setFlaggingId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");

  // Drag-resizable panel size (persisted) + a transient left offset so the
  // left-edge handle keeps the panel's right edge fixed while dragging.
  const [size, setSize] = useState<PanelSize>(loadPanelSize);
  const [leftOffset, setLeftOffset] = useState(0);

  // Re-anchor to the badge (left:0) each time the panel opens; the width
  // persists, the transient drag offset does not.
  useEffect(() => {
    if (open) setLeftOffset(0);
  }, [open]);

  // Begin an edge/corner resize. `edge` is any of "l","r","b" combined
  // ("br" = bottom-right corner). Width/height update live; the final size
  // is persisted on pointer-up.
  function startResize(
    e: ReactPointerEvent,
    edge: "l" | "r" | "b" | "br",
  ) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.w;
    const startH = size.h;
    const startLeft = leftOffset;
    const max = panelMax();
    let latest: PanelSize = { w: startW, h: startH };
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      let w = startW;
      let h = startH;
      let left = startLeft;
      if (edge === "r" || edge === "br") w = startW + dx;
      if (edge === "l") {
        w = clamp(startW - dx, PANEL_MIN_W, max.w);
        // Keep the right edge fixed: left tracks the width change.
        left = startLeft + startW - w;
      }
      if (edge === "b" || edge === "br") h = startH + dy;
      w = clamp(w, PANEL_MIN_W, max.w);
      h = clamp(h, PANEL_MIN_H, max.h);
      latest = { w, h };
      setSize(latest);
      setLeftOffset(left);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      savePanelSize(latest);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // One-time "how to review findings" banner. Dismissal persists across
  // sessions in localStorage; guarded so SSR / blocked-storage never throws.
  const [helpDismissed, setHelpDismissed] = useState(() => {
    try {
      return localStorage.getItem(FINDINGS_HELP_KEY) === "1";
    } catch {
      return false;
    }
  });
  const dismissHelp = () => {
    setHelpDismissed(true);
    try {
      localStorage.setItem(FINDINGS_HELP_KEY, "1");
    } catch {
      /* storage unavailable — banner just reappears next session */
    }
  };

  // Close the findings panel on Escape or a click outside it. Listeners
  // are only attached while the panel is open.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (verifying) {
    return (
      <span className="validation-badge validation-badge-verifying">
        <span className="spinner" /> Verifying content…
      </span>
    );
  }
  if (!validation) return null;

  const actions = actionMap(validation);
  const notes = noteMap(validation);
  const status = effectiveStatus(validation, actions);
  const active = activeFindings(validation, actions);
  const errors = active.filter((i) => i.severity === "error").length;
  const warns = active.filter((i) => i.severity === "warning").length;

  const label =
    status === "pass"
      ? "✓ Verified"
      : status === "warn"
        ? `⚠ ${warns} note${warns === 1 ? "" : "s"}`
        : `✗ ${errors} issue${errors === 1 ? "" : "s"}`;

  const hasFindings = active.length > 0;
  const failRemaining = unaddressedFailCount(validation, actions);
  const gateOk = failGateSatisfied(validation, actions);
  // Set when background Tier-2 verification didn't finish (poll timeout
  // / stale pending). Tier-1 results above still stand.
  const note = validation.note?.trim() || null;

  // Rows: every non-dismissed finding, keyed by stable id.
  const rows = validation.issues
    .map((f, i) => ({ f, id: findingId(f, i) }))
    .filter(({ id }) => actions[id] !== "dismissed");

  // Every finding has been acted on (dismissed / accepted / flagged) →
  // the footer offers a confident "Done"; otherwise "Close".
  const allAddressed = validation.issues.every(
    (f, i) => !!actions[findingId(f, i)],
  );

  return (
    <span
      ref={rootRef}
      className={`validation-badge-wrap status-${status}${compact ? " compact" : ""}`}
    >
      <button
        type="button"
        className={`validation-badge status-${status}`}
        onClick={() => hasFindings && setOpen((v) => !v)}
        aria-expanded={open}
        title={
          note
            ? note
            : hasFindings
              ? "Click to review findings"
              : "All checks passed"
        }
      >
        {label}
        {hasFindings && (
          <span className="validation-badge-caret">{open ? " ▴" : " ▾"}</span>
        )}
      </button>
      {note && <span className="validation-badge-note">{note}</span>}

      {open && hasFindings && (
        <div
          className="validation-findings-panel"
          role="dialog"
          style={{ width: size.w, height: size.h, left: leftOffset }}
        >
          <div className="validation-findings-header">
            <button
              type="button"
              className="validation-findings-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>
          <div className="validation-findings-body">
            {!helpDismissed && (
              <div className="validation-findings-help">
                <p>
                  Review each finding below. Dismiss false positives, accept
                  known issues, or flag items that need correction. Export is
                  enabled once all flagged issues are resolved.
                </p>
                <button
                  type="button"
                  className="validation-findings-help-dismiss"
                  onClick={dismissHelp}
                  aria-label="Dismiss help"
                  title="Got it"
                >
                  Got it
                </button>
              </div>
            )}
            {status === "fail" && !gateOk && (
              <div className="validation-findings-gate">
                ⚠ {failRemaining} issue{failRemaining === 1 ? "" : "s"} need
                review (dismiss or accept each) to enable export / copy.
              </div>
            )}
            <ul className="validation-findings-list">
            {rows.map(({ f, id }) => {
              const act = actions[id];
              return (
                <li
                  key={id}
                  className={`validation-finding sev-${f.severity}${
                    act ? ` acted-${act}` : ""
                  }`}
                >
                  <div className="validation-finding-head">
                    <span className="validation-finding-type">
                      {findingTitle(f.type)}
                    </span>
                    {act && (
                      <span className="validation-finding-state">{act}</span>
                    )}
                  </div>
                  <div className="validation-finding-desc">{f.description}</div>
                  <FindingContextCard finding={f} />
                  {act === "flagged" && notes[id] && flaggingId !== id && (
                    <div className="finding-flag-saved-note">
                      <span className="finding-flag-saved-label">
                        Your note:
                      </span>{" "}
                      {notes[id]}
                    </div>
                  )}
                  {onAction && (
                    <div className="validation-finding-actions">
                      <button
                        type="button"
                        className="finding-act dismiss"
                        title="This finding is incorrect — remove it (false positive)"
                        onClick={() => {
                          setFlaggingId(null);
                          onAction(id, "dismissed");
                        }}
                      >
                        Dismiss
                      </button>
                      <button
                        type="button"
                        className={`finding-act accept${act === "accepted" ? " on" : ""}`}
                        title="This finding is correct but I'm okay with it — acknowledge and proceed"
                        onClick={() => {
                          setFlaggingId(null);
                          onAction(id, "accepted");
                        }}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className={`finding-act flag${
                          act === "flagged" || flaggingId === id ? " on" : ""
                        }`}
                        title="This needs to be fixed — mark for attention"
                        onClick={() => {
                          // Open the note input rather than flagging
                          // immediately, so the user can add a correction note.
                          setNoteDraft(notes[id] ?? "");
                          setFlaggingId(id);
                        }}
                      >
                        Flag for edit
                      </button>
                    </div>
                  )}
                  {onAction && flaggingId === id && (
                    <div className="finding-flag-note">
                      <textarea
                        className="finding-flag-note-input"
                        placeholder="Add a correction note..."
                        value={noteDraft}
                        rows={2}
                        autoFocus
                        onChange={(e) => setNoteDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            onAction(id, "flagged", noteDraft);
                            setFlaggingId(null);
                          } else if (e.key === "Escape") {
                            setFlaggingId(null);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="finding-act flag on"
                        title="Flag this finding for correction on the next regenerate"
                        onClick={() => {
                          onAction(id, "flagged", noteDraft);
                          setFlaggingId(null);
                        }}
                      >
                        Flag
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
            </ul>
          </div>
          <div className="validation-findings-footer">
            <button
              type="button"
              className={`validation-findings-done${
                allAddressed ? " all-addressed" : ""
              }`}
              onClick={() => setOpen(false)}
              title={
                allAddressed
                  ? "All findings addressed — close"
                  : "Some findings still need review"
              }
            >
              {allAddressed ? "Done" : "Close"}
            </button>
          </div>
          {/* Drag handles: left/right edges (ew), bottom edge (ns),
              bottom-right corner (nwse). */}
          <div
            className="vfp-resize vfp-resize-l"
            onPointerDown={(e) => startResize(e, "l")}
          />
          <div
            className="vfp-resize vfp-resize-r"
            onPointerDown={(e) => startResize(e, "r")}
          />
          <div
            className="vfp-resize vfp-resize-b"
            onPointerDown={(e) => startResize(e, "b")}
          />
          <div
            className="vfp-resize vfp-resize-br"
            onPointerDown={(e) => startResize(e, "br")}
          />
        </div>
      )}
    </span>
  );
}
